/**
 * Font loading utilities for the design system
 */
import { withAssetPrefix } from 'gatsby';

export interface FontConfig {
  family: string;
  weights: number[];
  styles?: string[];
  display?: 'auto' | 'block' | 'swap' | 'fallback' | 'optional';
}

export const FONT_CONFIGS: Record<string, FontConfig> = {
  'whitenice': {
    family: 'whitenice',
    weights: [400],
    styles: ['normal'],
    display: 'swap',
  },
  'littledays': {
    family: 'littledays',
    weights: [400],
    styles: ['normal'],
    display: 'swap',
  },
};

/**
 * Font filename mapping - maps font family to actual filename
 */
const FONT_FILENAMES: Record<string, string> = {
  'whitenice': 'Whitenice.woff2',
  'littledays': 'Littledays.woff2',
};

/**
 * Creates @font-face declarations dynamically
 */
export const createFontFaceDeclarations = (fontFamilies: string[] = ['whitenice']): void => {
  if (typeof window === 'undefined') return;

  const blocks: string[] = [];
  const resolvedFamilies: string[] = [];

  fontFamilies.forEach((family) => {
    const config = FONT_CONFIGS[family];
    const filename = FONT_FILENAMES[family];

    if (!config || !filename) return;

    const fontUrl = withAssetPrefix(`/fonts/${filename}`);
    blocks.push(`
      @font-face {
        font-family: "${family}";
        src: url("${fontUrl}") format("woff2");
        font-weight: ${config.weights[0]};
        font-style: ${config.styles?.[0] || 'normal'};
        font-display: ${config.display || 'swap'};
      }
    `.trim());
    resolvedFamilies.push(family);
  });

  if (!blocks.length) return;

  // CP-FIX: une seule balise <style> → les deux URLs @font-face sont découvertes dans le même
  // bloc de parse CSS → fetch Whitenice/Littledays en parallèle (chaîne critique plus courte).
  const style = document.createElement('style');
  style.textContent = blocks.join('\n');
  style.setAttribute('data-font-families', resolvedFamilies.join(','));
  document.head.appendChild(style);
};

/**
 * Preloads fonts for better performance
 */
export const preloadFonts = (fontFamilies: string[] = ['whitenice']): void => {
  if (typeof window === 'undefined') return;

  fontFamilies.forEach(family => {
    const config = FONT_CONFIGS[family];
    const filename = FONT_FILENAMES[family];
    
    if (!config || !filename) return;

    // Use the actual filename instead of constructing with weight
    const fontUrl = withAssetPrefix(`/fonts/${filename}`);
    
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'font';
    link.type = 'font/woff2';
    link.crossOrigin = 'anonymous';
    link.href = fontUrl;
    
    document.head.appendChild(link);
  });
};

/**
 * Checks if a font is loaded
 */
export const isFontLoaded = (fontFamily: string): boolean => {
  if (typeof document === 'undefined') return false;
  
  try {
    return document.fonts.check(`16px "${fontFamily}"`);
  } catch {
    return false;
  }
};

/**
 * Waits for a font to be loaded
 */
export const waitForFont = (fontFamily: string, timeout: number = 3000): Promise<boolean> => {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') {
      resolve(false);
      return;
    }

    const checkFont = () => {
      if (isFontLoaded(fontFamily)) {
        resolve(true);
        return;
      }
      
      setTimeout(checkFont, 100);
    };
    
    checkFont();
    
    // Timeout fallback
    setTimeout(() => resolve(false), timeout);
  });
};

/**
 * Loads fonts with fallback
 */
export const loadFonts = async (fontFamilies: string[] = ['whitenice']): Promise<void> => {
  if (typeof document === 'undefined') return;

  const loadPromises = fontFamilies.map(async (family) => {
    const config = FONT_CONFIGS[family];
    if (!config) return;

    try {
      // Use the correct weight from config (first weight in the array)
      const weight = config.weights[0];
      const style = config.styles?.[0] || 'normal';
      await document.fonts.load(`${weight} ${style} 16px "${family}"`);
      if (process.env.NODE_ENV !== "production") {
        console.log(
          `✅ Font ${family} (weight: ${weight}, style: ${style}) loaded successfully`
        );
      }
    } catch (error) {
      console.warn(`⚠️ Failed to load font ${family}:`, error);
    }
  });

  await Promise.all(loadPromises);
};
